import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import Logo from '../atoms/Logo';
import NavLink from '../molecules/NavLink';
import Button from '../atoms/Button';

const Navbar = () => {
    const [activeSection, setActiveSection] = useState('home');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    useEffect(() => {
        const handleScroll = () => {
            // Re-ordered sections array to match new visual order if needed, or keep original
            const sections = ['home', 'difference', 'services', 'reviews', 'gallery'];
            const scrollPosition = window.scrollY + 100;

            for (const section of sections) {
                const element = document.getElementById(section);
                if (element) {
                    const { offsetTop, offsetHeight } = element;
                    if (
                        scrollPosition >= offsetTop &&
                        scrollPosition < offsetTop + offsetHeight
                    ) {
                        setActiveSection(section);
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToBooking = () => {
        const bookingSection = document.getElementById('booking');
        if (bookingSection) {
            bookingSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleMobileNavClick = (e, sectionId) => {
        e.preventDefault();
        setIsMobileMenuOpen(false);
        const section = document.getElementById(sectionId);
        if (section) {
            setTimeout(() => {
                section.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    };

    return (
        <nav className="fixed top-0 w-full z-50 bg-[#F9F7F2]/90 backdrop-blur-md border-b border-gray-200 px-6 py-4 transition-all">
            <div className="container mx-auto flex justify-between items-center">
                <Logo />

                {/* Desktop Menu */}
                <div className="hidden md:flex space-x-10 text-sm font-medium tracking-wide text-gray-600">
                    <NavLink href="#home" isActive={activeSection === 'home'}>Home</NavLink>
                    <NavLink href="#difference" isActive={activeSection === 'difference'}>The Difference</NavLink>
                    <NavLink href="#services" isActive={activeSection === 'services'}>Services</NavLink>
                    <NavLink href="#reviews" isActive={activeSection === 'reviews'}>Reviews</NavLink>
                    <NavLink href="#gallery" isActive={activeSection === 'gallery'}>Lookbook</NavLink>
                </div>

                <div className="flex items-center gap-4">
                    <Button
                        onClick={scrollToBooking}
                        variant="primary"
                        className="hidden md:block px-6 py-2.5 text-xs"
                    >
                        Book Now
                    </Button>

                    {/* Mobile Menu Toggle */}
                    <button
                        type="button"
                        className="md:hidden p-3 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none z-50 relative"
                        onClick={() => {
                            console.log('Mobile menu clicked, current state:', isMobileMenuOpen);
                            setIsMobileMenuOpen(!isMobileMenuOpen);
                        }}
                        aria-label="Toggle mobile menu"
                        aria-expanded={isMobileMenuOpen}
                    >
                        {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                    </button>
                </div>
            </div>

            {/* Fullscreen Mobile Menu Overlay */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="md:hidden fixed inset-0 top-0 left-0 w-full h-screen bg-[#F9F7F2] z-40 flex flex-col"
                    >
                        {/* Header with Logo and Close */}
                        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                            <Logo />
                            <button
                                type="button"
                                className="p-3 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                onClick={() => setIsMobileMenuOpen(false)}
                                aria-label="Close menu"
                            >
                                <X size={28} />
                            </button>
                        </div>

                        {/* Navigation Links */}
                        <div className="flex-1 flex flex-col justify-center items-center px-8 space-y-6">
                            {[
                                { id: 'home', label: 'Home' },
                                { id: 'difference', label: 'The Difference' },
                                { id: 'services', label: 'Services' },
                                { id: 'reviews', label: 'Reviews' },
                                { id: 'gallery', label: 'Lookbook' },
                            ].map((item, index) => (
                                <motion.a
                                    key={item.id}
                                    href={`#${item.id}`}
                                    onClick={(e) => handleMobileNavClick(e, item.id)}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 + index * 0.08, duration: 0.4 }}
                                    className={`text-3xl md:text-4xl font-medium tracking-wide transition-colors ${activeSection === item.id
                                            ? 'text-purple-900 font-bold'
                                            : 'text-gray-700 hover:text-purple-700'
                                        }`}
                                >
                                    {item.label}
                                </motion.a>
                            ))}
                        </div>

                        {/* Footer with CTA */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5, duration: 0.4 }}
                            className="px-8 py-8 border-t border-gray-200"
                        >
                            <Button
                                onClick={() => {
                                    setIsMobileMenuOpen(false);
                                    setTimeout(() => {
                                        scrollToBooking();
                                    }, 100);
                                }}
                                variant="primary"
                                className="w-full py-5 text-base"
                            >
                                Book Appointment
                            </Button>
                            <p className="text-center text-sm text-gray-500 mt-4">
                                @rich.trannnnnails
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};

export default Navbar;
